// The side menu's items and sub-items, and which is open — TODO T17 (30 Aug 2026), the CRM's
// children at T58, Candidates' four at T273 (5 Oct 2026). Pure data and two predicates, so a test can
// hold the structure without drawing it (`nav.test.ts`); `Shell.tsx` draws it.
import type { ConsoleCounts } from '@vera/bff';
import type { IconName } from '../ui/atoms.tsx';

/**
 * Each count says what it counts. "Conversations" carries the OPEN WINDOWS (design doc § T36.2)
 * rather than a bare number a recruiter would read as "threads", and says so.
 *
 * CRM (T58, 8 Sep 2026) is a group, not a screen: Clients moved inside it, with Potential clients
 * and Sales beside it, and the three appear under the CRM row while any of them is open. The row
 * itself opens the first tab. Natanel: "click on CRM, click on potential clients".
 */
/**
 * A nav count: a bare number, or a number with its word — "12 open", "1 waiting" — and, for the
 * one count that asks for a person (T71), an alert. The number and the word are drawn apart
 * (T71a, 15 Sep 2026): the numeral in the count pill's own type, the word in the UI's, so the
 * pill reads as a count with a label and not as a code token, and never wraps.
 */
export type NavCount = string | { n: number; word: string; alert?: true };

export interface NavChild {
  to: string;
  label: string;
  count: (counts: ConsoleCounts) => string;
  /** Whether this sub-item is the page open, where the path alone (`end`) does not say. */
  active?: (pathname: string) => boolean;
}

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  count?: (counts: ConsoleCounts) => NavCount;
  children?: NavChild[];
  /** The paths the group stays open under, when they are not just `to`'s own. */
  under?: string[];
}

export const NAV: NavItem[] = [
  // T273 (5 Oct 2026, Natanel: "keep the candidates tab, when you press on the candiates tab in the
  // side menu then ... subtabs should drop down"): Searches moved inside Candidates, with Proactive,
  // No-man's-land and All beside it, each its own count, drawn as CRM's are. The row opens on
  // Searches, the brain's home screen; `/searches` and `/candidates` still open where they did.
  {
    to: '/searches',
    label: 'Candidates',
    icon: 'users',
    under: ['/searches', '/candidates'],
    children: [
      {
        to: '/searches',
        label: 'Searches',
        count: (c) => String(c.activeSearches),
        active: (path) => path === '/searches' || path.startsWith('/searches/'),
      },
      {
        to: '/candidates/proactive',
        label: 'Proactive',
        count: (c) => String(c.proactive),
        active: (path) => path === '/candidates/proactive',
      },
      {
        to: '/candidates/no-mans-land',
        label: 'No-man’s-land',
        count: (c) => String(c.nomansLand),
        active: (path) => path === '/candidates/no-mans-land',
      },
      {
        to: '/candidates',
        label: 'All',
        count: (c) => String(c.candidates),
        // A person's own page, the entry form and their profile are All's too: they are reached from it.
        active: (path) =>
          path.startsWith('/candidates') &&
          path !== '/candidates/proactive' &&
          path !== '/candidates/no-mans-land',
      },
    ],
  },
  {
    to: '/crm',
    label: 'CRM',
    icon: 'building',
    children: [
      { to: '/crm/clients', label: 'Clients', count: (c) => String(c.clients) },
      {
        to: '/crm/potential',
        label: 'Potential clients',
        count: (c) => String(c.potentialClients),
      },
      { to: '/crm/sales', label: 'Sales', count: (c) => String(c.salesClients) },
      // T111: people, not companies — the team's LinkedIn networks, nobody already in the brain.
      {
        to: '/crm/connections',
        label: 'Connections',
        count: (c) => c.connections.toLocaleString('en-GB'),
      },
    ],
  },
  {
    to: '/conversations',
    label: 'Conversations',
    icon: 'wa',
    // T71: "N waiting" outranks "N open" the moment somebody writes into a paused thread — the
    // one nav number that asks the recruiter for something (Natanel, 15 Sep 2026).
    count: (c) =>
      c.waitingOnYou > 0
        ? { n: c.waitingOnYou, word: 'waiting', alert: true }
        : { n: c.openWindows, word: 'open' },
  },
  // T91: who kadmon would put forward, across every client. Beside Conversations, not under CRM:
  // the CRM's sub-items show only inside the CRM, and this number asks for somebody — the
  // second one that does — so it has to be seen from every page. No count while nothing waits.
  {
    to: '/proposed',
    label: 'Proposed',
    icon: 'sparkle',
    count: (c) =>
      c.proposedWaiting > 0 ? { n: c.proposedWaiting, word: 'waiting', alert: true } : '',
  },

  // Research (T53) carries no count: what would be counted is proposals waiting for a decision,
  // and that number is on the tab itself, per run, where the decision is made.
  { to: '/research', label: 'Research', icon: 'globe' },

  // Meetings (T70) carries no count either: the number worth showing is Unfiled, and it is on
  // the tab itself, above the list, where the filing is done.
  { to: '/meetings', label: 'Meetings', icon: 'doc' },

  // Reports carries no count: every number on it is windowed, and a bare badge beside "Reports"
  // would be a number without the window that gives it meaning (T32).
  { to: '/reports', label: 'Reports', icon: 'chart' },

  // T328: the person's own list, texted to kadmon in fixed words and kept here. No count: it is theirs.
  { to: '/list', label: 'My list', icon: 'check' },

  { to: '/settings', label: 'Settings', icon: 'settings' },
];

/** A group is open while the page is under it: its own path, or any of `under`. */
export function groupOpen(item: NavItem, pathname: string): boolean {
  if (item.children === undefined) return false;
  const roots = item.under ?? [item.to];
  return roots.some((root) => pathname === root || pathname.startsWith(`${root}/`));
}

/** A sub-item is the page open: its own predicate, or exactly its path. */
export function childActive(child: NavChild, pathname: string): boolean {
  return child.active ? child.active(pathname) : pathname === child.to;
}
